import { FC, useEffect, useState } from "react";
import useStyles from "../../../lead/quote/leadEstimateStyle";
import { Grid, Box } from "@mui/material";

import closeIcon from "../../../../../assets/closeModel.png";
import checkedIcon from "../../../../../assets/check-mark.png";

import Notification from "../../../../../components/common/notification/Notification";

import {
  ContractData,
  SelectedSite,
  ContractUnit,
  StorageRow,
  UnitRow,
} from "./Types";

import StepHeader from "./StepHeader";
import CustomerInfoBar from "./CustomerInfoBar";
import ContractDetailsStep from "./ContractDetailsStep";
import ContractAction from "./ContractAction";
import ManageSelectStorage from "./ManageSelectStorage";

import { useDispatch, useSelector } from "react-redux";

import {
  clearSingleContractDetailAction,
  createContractAction,
  initialContractState,
  updateContractAction,
} from "../../../../../store/reducers/contractStorageSlice";

import { contractValidateForm } from "./contractsUtils";

interface StorageContractFormProps {
  show: boolean;
  setShow: (show: boolean) => void;
  screenType: string;
}

const StorageContractForm: FC<StorageContractFormProps> = ({
  show,
  setShow,
  screenType,
}) => {
  const dispatch = useDispatch();

  const { storageDetails } = useSelector((state: any) => state.storage);

  const {
    singleContractDetail,
    isCreateContractResponse,
    customerStorageResponse,
  } = useSelector((state: any) => state.contractStorage);

  const [load, setLoad] = useState(false);

  const classes = useStyles({
    show,
    load,
  });

  // ============================================================
  // CONTRACT DATA
  // ============================================================

  const [contractData, setContractData] = useState<any>(initialContractState);

  const [selectedSites, setSelectedSites] = useState<SelectedSite[]>([]);

  // ============================================================
  // STORAGE SELECTION MODAL
  // ============================================================

  const [selectStorageOpen, setSelectStorageOpen] = useState(false);

  const [selectionStep, setSelectionStep] = useState(0);

  // ============================================================
  // LOAD EXISTING CONTRACT
  // ============================================================

  useEffect(() => {
    if (singleContractDetail?.id) {
      setContractData(singleContractDetail);

      /**
       * If API already returns sites, restore them.
       *
       * Expected API structure:
       *
       * sites: [
       *   {
       *     siteId: "...",
       *     units: [...]
       *   }
       * ]
       */

      if (
        Array.isArray(singleContractDetail?.sites) &&
        singleContractDetail.sites.length > 0
      ) {
        setSelectedSites(singleContractDetail.sites);
      }
    }
  }, [singleContractDetail]);

  // ============================================================
  // CLOSE
  // ============================================================

  const handleClose = () => {
    // Close contract form
    setShow(false);

    // Reset contract form data
    setContractData({
      ...initialContractState,
    });

    // Clear selected storage sites and units
    setSelectedSites([]);

    // Close storage selection modal
    setSelectStorageOpen(false);

    // Reset selection step
    setSelectionStep(0);

    // Clear validation errors
    setErrors({});

    // Reset loading state
    setLoad(false);
    dispatch(clearSingleContractDetailAction());
  };

  // ============================================================
  // CUSTOMER NAME
  // ============================================================

  const customerName =
    storageDetails?.customer_name ??
    storageDetails?.customerName ??
    storageDetails?.customer?.name ??
    storageDetails?.lead?.customer_name ??
    customerStorageResponse?.removalCustomer?.name ??
    "";

  // ============================================================
  // VALIDATION ERRORS
  // ============================================================

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ============================================================
  // CONTRACT FIELD CHANGE
  // ============================================================

  const handleContractFieldChange = (
    field: keyof ContractData,
    value: string | boolean,
  ) => {
    setContractData((prev: any) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((currentErrors) => {
      const nextErrors = {
        ...currentErrors,
      };

      delete nextErrors[field];

      return nextErrors;
    });
  };

  // ============================================================
  // CREATE CONTRACT UNIT
  // ============================================================

  const createContractUnit = (unit: UnitRow): ContractUnit => {
    return {
      unitId: unit.id,

      unitRefId: unit.unit_ref_id,
      unitTypeName: unit.unit_type_name,
      storageType: unit.storage_type,
      sizeFt3: unit.size_ft3,

      pricePerUnit: unit.price_per_unit,
      rentType: unit.rent_type,

      /**
       * Keep existing values if available.
       *
       * Otherwise initialise empty values.
       */
      inDate: unit.inDate || unit.in || "",
      inTime: unit.inTime || "",

      outDate: unit.outDate || unit.out || "",
      outTime: unit.outTime || "",

      provisional: unit.provisional ?? false,

      invoicedUpto: unit.invoicedUpto ?? unit.invoicedUpTo ?? null,

      invoiceLineDescription: unit.invoiceLineDescription || "",
    };
  };

  // ============================================================
  // MERGE UNITS
  //
  // Important:
  // Existing unit data must NOT be overwritten.
  //
  // Example:
  //
  // User selected Unit 1
  // entered:
  //
  // In Date = 07 Sep
  // In Time = 09:30
  //
  // Then opens storage again.
  //
  // Unit 1 should keep 07 Sep / 09:30.
  // ============================================================

  const mergeUnits = (
    existingUnits: ContractUnit[],
    newUnits: UnitRow[],
  ): ContractUnit[] => {
    const existingMap = new Map<string, ContractUnit>();

    existingUnits.forEach((unit) => {
      existingMap.set(unit.unitId, unit);
    });

    newUnits.forEach((unit) => {
      const existingUnit = existingMap.get(unit.id);

      /**
       * If unit already exists:
       *
       * Keep existing entered data.
       *
       * Only update API metadata if required.
       */
      if (existingUnit) {
        existingMap.set(unit.id, {
          ...existingUnit,

          unitRefId: unit.unit_ref_id || existingUnit.unitRefId,

          unitTypeName: unit.unit_type_name || existingUnit.unitTypeName,

          storageType: unit.storage_type || existingUnit.storageType,

          sizeFt3: unit.size_ft3 || existingUnit.sizeFt3,

          pricePerUnit: unit.price_per_unit || existingUnit.pricePerUnit,

          rentType: unit.rent_type || existingUnit.rentType,
        });

        return;
      }

      /**
       * New unit.
       */
      existingMap.set(unit.id, createContractUnit(unit));
    });

    return Array.from(existingMap.values());
  };

  // ============================================================
  // ASSIGN STORAGE
  //
  // IMPORTANT:
  //
  // OLD:
  //
  // setSelectedStorage(storage);
  // setSelectedUnits(units);
  //
  // This replaced previous selection.
  //
  // NEW:
  //
  // Add/update site inside selectedSites.
  // ============================================================

  const handleAssignStorage = (storage: StorageRow, units: UnitRow[]) => {
    setSelectedSites((previousSites) => {
      const existingSiteIndex = previousSites.findIndex(
        (site) => site.siteId === storage.id,
      );

      const siteId = storage.id || "";

      // ========================================================
      // EXISTING SITE
      // ========================================================

      if (existingSiteIndex !== -1) {
        const updatedSites = [...previousSites];

        const existingSite = updatedSites[existingSiteIndex];

        updatedSites[existingSiteIndex] = {
          ...existingSite,

          siteName: storage.site_name ?? existingSite.siteName,

          siteType: storage.site_type ?? existingSite.siteType,

          units: mergeUnits(existingSite.units, units),
        };

        return updatedSites;
      }

      // ========================================================
      // NEW SITE
      // ========================================================

      const newSite: SelectedSite = {
        siteId,

        siteName: storage.site_name || "",

        siteType: storage.site_type || "",

        units: units.map(createContractUnit),
      };

      return [...previousSites, newSite];
    });

    // Move to details step.
    setSelectionStep(3);

    // Close selection popup.
    setSelectStorageOpen(false);
  };

  // ============================================================
  // UPDATE UNIT FIELD
  //
  // This is VERY IMPORTANT.
  //
  // In/Out/Provisional/etc. changes are stored inside
  // selectedSites[].units[].
  // ============================================================

  const handleUnitChange = (
    unitId: string,
    field: string,
    value: string | boolean | null,
  ) => {
    setSelectedSites((previousSites) =>
      previousSites.map((site) => ({
        ...site,

        units: site.units.map((unit) =>
          unit.unitId === unitId
            ? {
                ...unit,
                [field]: value,
              }
            : unit,
        ),
      })),
    );
  };

  // ============================================================
  // REMOVE UNIT
  //
  // Optional helper for future use.
  // ============================================================

  const handleRemoveUnit = (siteId: string, unitId: string) => {
    setSelectedSites((previousSites) => {
      return previousSites
        .map((site) => {
          if (site.siteId !== siteId) {
            return site;
          }

          return {
            ...site,
            units: site.units.filter((unit) => unit.unitId !== unitId),
          };
        })
        .filter((site) => site.units.length > 0);
    });
  };

  // ============================================================
  // REMOVE SITE
  //
  // Optional helper.
  // ============================================================

  const handleRemoveSite = (siteId: string) => {
    setSelectedSites((previousSites) =>
      previousSites.filter((site) => site.siteId !== siteId),
    );
  };

  // ============================================================
  // NUMBER HELPER
  // ============================================================

  const toNumberOrNull = (value: string | number | null | undefined) => {
    if (value === "" || value === null || value === undefined) {
      return null;
    }

    const numberValue = Number(value);

    return Number.isNaN(numberValue) ? null : numberValue;
  };

  // ============================================================
  // BUILD API SITES PAYLOAD
  //
  // UI may have lots of metadata.
  //
  // API only receives required fields.
  // ============================================================

  const buildSitesPayload = () => {
    return selectedSites.map((site) => ({
      siteId: site.siteId,

      units: site.units.map((unit) => ({
        unitId: unit.unitId,

        inDate: unit.inDate || null,

        inTime: unit.inTime || null,

        outDate: unit.outDate || null,

        outTime: unit.outTime || null,

        provisional: unit.provisional ?? false,

        invoicedUpto: unit.invoicedUpto ?? null,
      })),
    }));
  };

  console.log("customerStorageResponse", customerStorageResponse);
  // ============================================================
  // SUBMIT
  // ============================================================

  const submitHandler = (event: React.MouseEvent<HTMLImageElement>) => {
    event.preventDefault();

    console.log("========== SUBMIT CLICKED ==========");
    console.log("singleContractDetail:", singleContractDetail);
    console.log("contractId:", singleContractDetail?.id);
    console.log("contractData:", contractData);
    console.log("selectedSites:", selectedSites);

    // ========================================================
    // CONTRACT VALIDATION
    // ========================================================

    const { localError, isValid } = contractValidateForm({
      ...contractData,
      customerName,
    });

    setErrors(localError);

    if (!isValid) {
      return;
    }

    // ========================================================
    // VALIDATE STORAGE SELECTION
    // ========================================================

    // if (selectedSites.length === 0) {
    //   setErrors((previous) => ({
    //     ...previous,
    //     sites: "Please select at least one storage site.",
    //   }));

    //   return;
    // }

    // const hasSelectedUnits = selectedSites.some(
    //   (site) => site.units.length > 0,
    // );

    // if (!hasSelectedUnits) {
    //   setErrors((previous) => ({
    //     ...previous,
    //     sites: "Please select at least one storage unit.",
    //   }));

    //   return;
    // }

    // ========================================================
    // FINAL PAYLOAD
    // ========================================================

    const payload = {
      ...contractData,

      customerName,

      leadId: storageDetails?.leadId ?? customerStorageResponse?.existingStorageCustomer?.lead_id ?? null,

      rate: toNumberOrNull(contractData.rate),

      insurancePercentage: toNumberOrNull(contractData.insurancePercentage),

      /**
       * IMPORTANT:
       *
       * All selected sites and units.
       */
      sites: buildSitesPayload(),
    };

    console.log("Create Contract Payload:", payload);

    // ========================================================
    // DEBUG SELECTED SITES
    // ========================================================

    console.log("Selected Sites:", selectedSites);

    console.log("Sites Payload:", buildSitesPayload());

    // ========================================================
    // API
    // ========================================================

    setLoad(true);
    console.log("singleContractDetail", singleContractDetail);
    if (singleContractDetail?.id) {
      // UPDATE CONTRACT
      const updatePayload = {
        ...payload,
        contractId: singleContractDetail.id,
      };

      console.log("Update Contract Payload:", updatePayload);

      dispatch(updateContractAction(updatePayload));
    } else {
      // CREATE CONTRACT
      console.log("Create Contract Payload:", payload);

      dispatch(createContractAction(payload));
    }
  };

  // ============================================================
  // TOTAL SELECTED UNITS
  // ============================================================

  const totalSelectedUnits = selectedSites.reduce(
    (total, site) => total + site.units.length,
    0,
  );

  // ============================================================
  // STEP CONTENT
  // ============================================================

  const renderStepContent = () => {
    return (
      <ContractDetailsStep
        contractData={contractData}
        customerName={customerName}
        onFieldChange={handleContractFieldChange}
        selectedSites={selectedSites}
        onSelectStorage={() => setSelectStorageOpen(true)}
        error={errors}
      />
    );
  };

  // ============================================================
  // CURRENT STEP
  // ============================================================

  const currentStep =
    totalSelectedUnits > 0 ? 3 : selectedSites.length > 0 ? 2 : selectionStep;

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      {/* ========================================================
          STORAGE SELECTION MODAL
      ======================================================== */}

      <ManageSelectStorage
        open={selectStorageOpen}
        onHide={() => {
          setSelectStorageOpen(false);
          setSelectionStep(0);
        }}
        onAssign={handleAssignStorage}
        onStepChange={setSelectionStep}
      />

      {/* ========================================================
          CONTRACT POPUP
      ======================================================== */}

      <div
        id="estimate-popup-fixed"
        className={classes.estimatePopupContainer}
        style={{
          zIndex: show ? 11 : -1,
        }}
      >
        <Grid container spacing={2}>
          {/* ====================================================
              HEADER
          ==================================================== */}

          <Grid
            item
            xs={12}
            sm={12}
            sx={{
              height: "auto",
              zIndex: 99,
              paddingLeft: "0px",
            }}
          >
            <div className={classes.popupTitle}>
              <Box sx={{ flex: 1 }}>
                <StepHeader activeStep={currentStep} />
              </Box>

              <Box>
                {/* SAVE */}
                <img
                  onClick={(e: any) => submitHandler(e)}
                  src={checkedIcon}
                  style={{
                    cursor: "pointer",
                    height: "26px",
                    width: "26px",
                    marginRight: 10,
                  }}
                  alt="save"
                />

                {/* CLOSE */}
                <img
                  src={closeIcon}
                  style={{
                    cursor: "pointer",
                    height: "26px",
                    width: "26px",
                  }}
                  onClick={handleClose}
                  alt="cross-icon"
                />
              </Box>
            </div>
          </Grid>

          {/* ====================================================
              CUSTOMER INFO
          ==================================================== */}

          <Grid
            item
            xs={12}
            sm={12}
            sx={{
              paddingLeft: "0px !important",
            }}
          >
            <CustomerInfoBar data={storageDetails} />
          </Grid>

          {/* ====================================================
              NOTIFICATION
          ==================================================== */}

          <Grid
            item
            xs={12}
            sm={12}
            sx={{
              opacity: load ? 1 : 0,
              paddingLeft: "0px !important",
            }}
          >
            <Notification />
          </Grid>

          {/* ====================================================
              CONTRACT DETAILS
          ==================================================== */}

          <Grid
            item
            xs={12}
            sm={12}
            sx={{
              paddingLeft: "0px !important",
              paddingBottom: "70px",
            }}
          >
            {renderStepContent()}
          </Grid>

          {/* ====================================================
              BOTTOM ACTION BAR
          ==================================================== */}

          <div
            style={{
              position: "fixed",
              borderTop: "1px solid #CCC",
              left: 100,
              bottom: 48,
              right: 100,
              background: "#FFF",
              padding: "10px 20px",
              zIndex: 1,
              border: "2px solid #CCC",
              boxShadow: "0 4px 6px -2px rgba(0,0,0,0.2)",
              borderBottomLeftRadius: "5px",
              borderBottomRightRadius: "5px",
            }}
          >
            <div
              style={{
                paddingLeft: "19px",
              }}
            >
              <ContractAction
                formData={null}
                isFormReadyOnly={false}
                isSaving={false}
                onSave={(e: any) => submitHandler(e)}
                onPrint={() => {}}
                onInvoiceNow={() => {}}
                onDeposit={() => {}}
                onSendForESign={() => {}}
                onClose={() => {}}
              />
            </div>
          </div>
        </Grid>
      </div>
    </>
  );
};

export default StorageContractForm;
